import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc, 
  query, 
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';

interface ContentItem {
  id: string;
  title: string;
  description: string;
  type: 'lesson' | 'quiz' | 'homework' | 'practice';
  subject: string;
  grade: string;
  createdAt: string;
  createdBy: string;
  authorName?: string;
}

const Content: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedGrade, setSelectedGrade] = useState('All');
  
  // Form state for adding content
  const [isAddingContent, setIsAddingContent] = useState(false);
  const [newContent, setNewContent] = useState({
    title: '',
    description: '',
    type: 'lesson' as 'lesson' | 'quiz' | 'homework' | 'practice',
    subject: 'Mathematics',
    grade: '1',
  });
  
  // Content types and subjects for dropdowns
  const contentTypes = ['lesson', 'quiz', 'homework', 'practice'];
  const subjects = ['Mathematics', 'Hungarian', 'Science', 'History', 'English', 'Art', 'Music', 'Physical Education'];
  
  // Fetch content from Firestore
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const contentRef = collection(db, 'content');
        const contentQuery = query(contentRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(contentQuery);
        
        const fetchedContent: ContentItem[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Set default values for any missing fields
          title: doc.data().title || t('content.untitled'),
          description: doc.data().description || '',
          type: doc.data().type || 'lesson',
          subject: doc.data().subject || 'Mathematics',
          grade: doc.data().grade || '1',
          createdAt: doc.data().createdAt || new Date().toISOString(),
          createdBy: doc.data().createdBy || 'unknown'
        } as ContentItem));
        
        setContentItems(fetchedContent);
      } catch (err) {
        console.error('Error fetching content:', err);
        setError(t('content.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [t]);
  
  // Add new content to Firestore
  const handleAddContent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newContent.title || !newContent.subject || !newContent.grade) {
      setError(t('validation.required'));
      return;
    }
    
    try {
      setLoading(true);
      
      // Add the content to Firestore
      const contentData = {
        ...newContent,
        createdAt: new Date().toISOString(),
        createdBy: userData?.uid,
        authorName: userData?.displayName || userData?.email
      };
      
      const docRef = await addDoc(collection(db, 'content'), contentData);
      
      // Add the new content to the local state
      setContentItems(prevContent => [
        { 
          id: docRef.id, 
          ...contentData,
        } as ContentItem,
        ...prevContent
      ]);
      
      // Reset the form
      setNewContent({
        title: '',
        description: '',
        type: 'lesson',
        subject: 'Mathematics',
        grade: '1',
      });
      
      setIsAddingContent(false);
    } catch (err) {
      console.error('Error adding content:', err);
      setError(t('content.failedToAdd'));
    } finally {
      setLoading(false);
    }
  };
  
  // Delete content from Firestore
  const handleDeleteContent = async (id: string) => {
    if (!window.confirm(t('content.confirmDelete'))) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete the content from Firestore
      await deleteDoc(doc(db, 'content', id));
      
      // Remove the content from the local state
      setContentItems(prevContent => prevContent.filter(item => item.id !== id));
    } catch (err) {
      console.error('Error deleting content:', err);
      setError(t('content.failedToDelete'));
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to get the color based on content type
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'lesson':
        return 'bg-blue-100 text-blue-800';
      case 'quiz':
        return 'bg-purple-100 text-purple-800';
      case 'homework':
        return 'bg-yellow-100 text-yellow-800';
      case 'practice':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Filter content items based on search and filters
  const filteredContent = contentItems.filter(item => {
    // Filter by search term
    const matchesSearch = 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by subject
    const matchesSubject = selectedSubject === 'All' || item.subject === selectedSubject;
    
    // Filter by type
    const matchesType = selectedType === 'All' || item.type === selectedType;
    
    // Filter by grade
    const matchesGrade = selectedGrade === 'All' || item.grade === selectedGrade;
    
    return matchesSearch && matchesSubject && matchesType && matchesGrade;
  });
  
  // Get unique grades for filter
  const grades = ['All', ...new Set(contentItems.map(item => item.grade))];
  
  // Display loading state
  if (loading && contentItems.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#309D97]"></div>
      </div>
    );
  }
  
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('content.management')}</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            className="float-right"
            onClick={() => setError('')}
          >
            &times;
          </button>
        </div>
      )}
      
      {/* Add Content Form */}
      {isAddingContent && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-lg font-medium mb-4">{t('content.addContent')}</h2>
          <form onSubmit={handleAddContent}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.title')}*</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newContent.title}
                  onChange={(e) => setNewContent({...newContent, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.description')}</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  rows={3}
                  value={newContent.description}
                  onChange={(e) => setNewContent({...newContent, description: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.type')}*</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newContent.type}
                  onChange={(e) => setNewContent({...newContent, type: e.target.value as any})}
                  required
                >
                  {contentTypes.map(type => (
                    <option key={type} value={type}>{t(`content.${type}`)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.subject')}*</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newContent.subject}
                  onChange={(e) => setNewContent({...newContent, subject: e.target.value})}
                  required
                >
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{t(`content.subjects.${subject}`)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.grade')}*</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newContent.grade}
                  onChange={(e) => setNewContent({...newContent, grade: e.target.value})}
                  required
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(grade => (
                    <option key={grade} value={grade.toString()}>{t('students.gradeNumber', { number: grade })}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => setIsAddingContent(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#309D97] text-white rounded-md hover:bg-opacity-90"
                disabled={loading}
              >
                {loading ? t('content.adding') : t('common.add')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Filters and search */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.search')}</label>
            <input
              type="text"
              placeholder={t('content.searchPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.subject')}</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="All">{t('content.allSubjects')}</option>
              {subjects.map(subject => (
                <option key={subject} value={subject}>{t(`content.subjects.${subject}`)}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.type')}</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="All">{t('content.allTypes')}</option>
              {contentTypes.map(type => (
                <option key={type} value={type}>{t(`content.${type}`)}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.grade')}</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
            >
              {grades.map(grade => (
                <option key={grade} value={grade}>
                  {grade === 'All' ? t('students.allGrades') : t('students.gradeNumber', { number: grade })}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {t('content.showing', { filtered: filteredContent.length, total: contentItems.length })}
          </div>
          
          {userData?.role === 'admin' || userData?.role === 'teacher' ? (
            <button 
              className="bg-[#309D97] text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition"
              onClick={() => setIsAddingContent(!isAddingContent)}
            >
              {isAddingContent ? t('common.cancel') : t('content.addContent')}
            </button>
          ) : null}
        </div>
      </div>
      
      {/* Content Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredContent.map(item => (
          <div key={item.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-medium text-gray-900 truncate flex-1">{item.title}</h3>
                <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(item.type)} ml-2`}>
                  {t(`content.${item.type}`)}
                </span>
              </div>
              
              <p className="text-sm text-gray-500 mb-4 line-clamp-2">{item.description}</p>
              
              <div className="flex flex-wrap text-xs text-gray-500 mb-3">
                <div className="mr-4">
                  <span className="font-medium">{t('content.subject')}:</span> {t(`content.subjects.${item.subject}`)}
                </div>
                <div>
                  <span className="font-medium">{t('content.grade')}:</span> {t('students.gradeNumber', { number: item.grade })}
                </div>
              </div>
              
              <div className="text-xs text-gray-500">
                <div>
                  <span className="font-medium">{t('content.author')}:</span> {item.authorName || t('content.unknown')}
                </div>
                <div>
                  <span className="font-medium">{t('content.createdAt')}:</span> {new Date(item.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-4 py-3 flex justify-between">
              <button className="text-[#309D97] text-sm font-medium hover:text-[#1e7d77]">
                {t('content.viewDetails')}
              </button>
              
              {(userData?.role === 'admin' || (userData?.role === 'teacher' && item.createdBy === userData.uid)) && (
                <button 
                  className="text-red-600 text-sm font-medium hover:text-red-800"
                  onClick={() => handleDeleteContent(item.id)}
                  disabled={loading}
                >
                  {t('content.delete')}
                </button>
              )}
            </div>
          </div>
        ))}
        
        {filteredContent.length === 0 && !loading && (
          <div className="md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow p-6 text-center text-gray-500">
            {contentItems.length === 0 
              ? t('content.noContent') 
              : t('content.noMatchingContent')}
          </div>
        )}
      </div>
    </div>
  );
};

export default Content; 